import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BidzPackages, PACKAGE_TERMS } from "../components/BidzPackages";
import { Coins, ShieldCheck, Ticket } from "lucide-react";

function CostTable() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s?.bidz_tiers) return null;
  const tiers = [...s.bidz_tiers].sort((a, b) => a.max_value - b.max_value);
  const passBands = [...(s.pass_bands || [])].sort((a, b) => a.max_value - b.max_value);
  const passFor = (max) => (passBands.find(b => max <= b.max_value) || { price: s.pass_above_price }).price;
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
  return (
    <table className="w-full text-sm" data-testid="bidz-cost-table">
      <thead className="text-left text-xs uppercase text-slate-500"><tr><th className="py-2">Reserve price (or starting bid)</th><th className="py-2 text-right">Bidz per bid</th><th className="py-2 text-right">Auction Pass</th></tr></thead>
      <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
        {tiers.map((t, i) => (
          <tr key={t.max_value}><td className="py-2">{i === 0 ? `Up to ${fmt(t.max_value)}` : `${fmt(tiers[i - 1].max_value + 1)} – ${fmt(t.max_value)}`}</td><td className="py-2 text-right font-bold">{t.bidz}</td><td className="py-2 text-right font-bold">{fmt(passFor(t.max_value))}</td></tr>
        ))}
        <tr><td className="py-2">Above {fmt(tiers[tiers.length - 1].max_value)}</td><td className="py-2 text-right font-bold">{s.bidz_above_max}</td><td className="py-2 text-right font-bold">{fmt(s.pass_above_price)}</td></tr>
      </tbody>
    </table>
  );
}

export default function BidzStore() {
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-12" data-testid="bidz-store-page">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 flex items-center gap-2"><Coins className="h-4 w-4" /> Bidz Credits</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold max-w-2xl">Bid with Bidz. Know the cost before every bid.</h1>
      <p className="mt-4 text-base text-slate-600 dark:text-slate-400 max-w-2xl">Each auction has a fixed Bidz cost per bid, based on its reserve price. The cost is locked when the auction goes live and is shown before you confirm.</p>
      <div className="mt-10"><BidzPackages /></div>
      <div className="mt-6 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-5" data-testid="bidz-pass-info">
        <div className="font-bold flex items-center gap-2"><Ticket className="h-4 w-4 text-amber-600" /> One-Time Auction Pass: pay per auction, no Bidz needed</div>
        <div className="text-sm text-slate-500 mt-1">For occasional bidders. Buy it from any live auction's bid screen: unlimited bids on that one auction, priced by its reserve band (see table below). Expires when the auction ends; not transferable.</div>
      </div>
      <div className="mt-14 grid lg:grid-cols-2 gap-8">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h2 className="text-lg font-bold mb-3">Bidz cost per bid & Auction Pass price</h2>
          <CostTable />
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Bidz rules</h2>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400 list-disc pl-5" data-testid="bidz-rules">
            {PACKAGE_TERMS.map(t => <li key={t}>{t}</li>)}
            <li>Package validity starts on successful payment. Payments are processed securely by Razorpay in INR.</li>
          </ul>
          <Link to="/buyer?tab=bidz" className="mt-4 inline-block text-amber-600 font-semibold text-sm" data-testid="bidz-store-wallet-link">View my Bidz wallet →</Link>
        </div>
      </div>
    </div>
  );
}
